// src/features/auth/components/AuthScreenLayout.tsx

import type { ReactNode } from 'react';
import { Anchor, Box, Container, Group, Image, Text, Title } from '@mantine/core';
import { Brand, BRAND_LEGAL_LINKS } from '@/shared/utils/brand';
import shLoginLogo from '@/assets/img/shlogo_login.png';

/**
 * Shared chrome for the unauthenticated screens (login, forgot, reset): a
 * full-height branded backdrop with the host-appropriate brand mark on top and
 * the host-appropriate Privacy / Terms links below. The brand variant is read
 * synchronously (Brand.variant), so a white-label visitor never sees a flash of
 * Streamhoster branding — the Streamhoster mark is only ever rendered on a
 * positively-matched streamhoster.com host.
 */
export function AuthScreenLayout({ children }: { children: ReactNode }) {
  const variant = Brand.variant();
  const legal = BRAND_LEGAL_LINKS[variant];

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--mantine-spacing-lg)',
        padding: 'var(--mantine-spacing-xl) var(--mantine-spacing-md)',
        background:
          'linear-gradient(180deg, var(--mantine-color-blue-0) 0%, var(--mantine-color-gray-1) 100%)',
      }}
    >
      <Group justify="center">
        {variant === 'streamhoster' ? (
          <Image src={shLoginLogo} alt="Streamhoster" w={200} fit="contain" />
        ) : (
          <Title order={1} c="blue.7" fw={700} tt="uppercase" style={{ letterSpacing: 1 }}>
            Media Login
          </Title>
        )}
      </Group>

      <Container size={420} w="100%" p={0}>
        {children}
      </Container>

      <Group gap={6} justify="center">
        <Anchor href={legal.privacy} target="_blank" rel="noopener noreferrer" size="xs" c="dimmed">
          Privacy
        </Anchor>
        <Text size="xs" c="dimmed">
          ·
        </Text>
        <Anchor href={legal.terms} target="_blank" rel="noopener noreferrer" size="xs" c="dimmed">
          Terms
        </Anchor>
      </Group>
    </Box>
  );
}
